import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import localStyles from './NodeListForSearchWidget.module.pcss';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import type { NodeSearchViewFragment, UserSearchViewFragment } from '../../../types/graphql-types';
import { EndUserQueryParams, EndUserComponent } from '@aurora/shared-types/pages/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import type { PagerVariantTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { PagerPosition, PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import type { ItemViewVariantProps } from '../../entities/types';
import type { ItemType, NodeViewVariant } from '../../../types/enums';
import NodeListForSearchTerm from '../../nodes/NodeListForSearchTerm/NodeListForSearchTerm';
import useTranslation from '../../useTranslation';

interface Props extends WidgetProps {
  /**
   * Class name(s) to apply to the wrapping element.
   */
  className?: string;
}

/**
 * Renders nodes list in search result page
 *
 * @author Kiran Kumari
 */
const NodeListForSearchWidget: WidgetFC<Props> = ({ className }) => {
  const cx = useClassNameMapper(localStyles);
  const { router } = useEndUserRoutes();
  const queryTerm = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_KEY);
  const { formatMessage } = useTranslation(EndUserComponent.NODE_LIST_FOR_SEARCH_TERM);

  const secondaryListPagerVariant: PagerVariantTypeAndProps<EndUserPages, EndUserQueryParams> = {
    type: PagerVariant.LOAD_MORE,
    props: {
      position: PagerPosition.START
    }
  };

  if (secondaryListPagerVariant) {
    secondaryListPagerVariant.props = secondaryListPagerVariant.props || {};
    secondaryListPagerVariant.props.ariaLabel = formatMessage('showMoreBoards');
  }

  /**
   * Function to get list variant types and props for secondary search list
   *
   * @returns Secondary ListVariantTypeAndProps
   */
  function getSecondaryListVariant<
    T extends NodeSearchViewFragment | UserSearchViewFragment
  >(): ListVariantTypeAndProps<T> {
    return {
      type: ListVariant.UNSTYLED,
      props: {
        listItemClassName: (): string => cx('lia-search-item')
      }
    };
  }

  const nodeViewVariantPropsOverrides: ItemViewVariantProps<
    ItemType.NODE_SEARCH,
    NodeViewVariant.INLINE
  > = {
    className: cx('lia-content-box lia-user-content-box'),
    useNodeTopicsCount: true,
    useEntireItemAsLink: true,
    useNodeTopicsCountIcon: false,
    useNeutralLabel: true
  };

  return (
    <NodeListForSearchTerm
      headerClassname={cx('lia-nodelist-header')}
      queryTerm={queryTerm}
      listVariant={getSecondaryListVariant<NodeSearchViewFragment>()}
      nodeViewVariantPropsOverrides={nodeViewVariantPropsOverrides}
      pagerVariant={secondaryListPagerVariant}
      panel={PanelType.STANDARD}
      useEmpty
      paneledListClassname={className}
    />
  );
};

export default NodeListForSearchWidget;
